// Shared SMS-preview logic for the appointment create/edit flows.
// Extracted verbatim from AppointmentModal.js so the modal and the appointment
// wizard produce byte-identical preview items and scheduling payloads.
// Item-id contract (consumed by getSchedulingFromPreview in both UIs):
//   single appointment: `single-initial`, `single-reminder`
//   series:             `series-initial` + per-occurrence `apt-<idx>-initial` / `apt-<idx>-reminder`

import { formatEnglishDate, formatHebrewDate, RECURRING_FREQUENCY } from "../firebase";
import { HEBREW_DAYS_FULL } from "./dateUtils";
import {
  MAX_SMS_LENGTH,
  SMS_LINK_CHARS as CATALOG_LINK_CHARS,
  renderSms,
} from "./smsTemplates";

// Re-exported from the SMS template catalog, which owns the wording and the
// length budget for both the preview and the actual send.
export const SMS_LINK_CHARS = CATALOG_LINK_CHARS;
export const SMS_MAX_LENGTH = MAX_SMS_LENGTH;

// Appointment date as the patient will read it, in their communication language.
export const formatSmsDate = (dateStr, language) =>
  language === "en" ? formatEnglishDate(dateStr) : formatHebrewDate(dateStr);

// Minimum gap between the confirmation SMS and the reminder SMS. Anything
// tighter and the patient gets two texts back to back, which reads as spam.
// Mirrored server-side in functions/index.js (MIN_SMS_GAP_MS).
export const MIN_SMS_GAP_MINUTES = 60;
export const MIN_SMS_GAP_MS = MIN_SMS_GAP_MINUTES * 60 * 1000;

export const SMS_SUPPRESS_REASON = {
  PAST_REMINDER: "מועד השליחה כבר עבר — התזכורת לא תישלח",
  PAST_INITIAL: "מועד השליחה כבר עבר — ההודעה לא תישלח",
  TOO_CLOSE:
    "התזכורת קרובה מדי להודעת האישור — לא תישלח כדי למנוע שתי הודעות רצופות",
};

// Hebrew label for a series frequency
export const getFrequencyLabel = (frequency) => {
  switch (frequency) {
    case RECURRING_FREQUENCY.WEEKLY:
      return "שבועי";
    case RECURRING_FREQUENCY.BIWEEKLY:
      return "אחת לשבועיים";
    case RECURRING_FREQUENCY.MONTHLY:
      return "חודשי";
    case RECURRING_FREQUENCY.TWICE_WEEKLY:
      return "פעמיים בשבוע";
    default:
      return "שבועי";
  }
};

// Local wall-clock parse of a preview item's send time. The stored format is the
// same naive `YYYY-MM-DDTHH:mm:00` string that gets written to the appointment
// doc, so the client and the server agree on the instant (Asia/Jerusalem).
export const parsePreviewSendTime = (item) => {
  if (!item?.scheduledDate || !item?.scheduledTime) return null;
  const dt = new Date(`${item.scheduledDate}T${item.scheduledTime}:00`);
  return isNaN(dt.getTime()) ? null : dt;
};

// Group key for the guards below: single appointment vs one recurring occurrence.
const smsPreviewGroupKey = (id) => {
  const m = id.match(/^apt-(\d+)-(?:initial|reminder)$/);
  if (m) return `apt-${m[1]}`;
  if (id === "single-initial" || id === "single-reminder") return "single";
  return null;
};

// Suppress SMS preview items that cannot or should not be sent:
//   - a send time that already passed (hard rule — the server drops these too)
//   - a reminder landing less than MIN_SMS_GAP_MINUTES after the confirmation
//     (soft rule — the therapist can override by re-checking the box)
// Returns a new array with `enabled` / `suppressedReason` applied.
export const applySmsScheduleGuards = (items, now = new Date()) => {
  const guarded = items.map((item) => {
    // Immediate messages are sent inline on create — nothing to validate.
    if (item.isImmediate) return { ...item, suppressedReason: null };

    const sendAt = parsePreviewSendTime(item);
    if (sendAt && sendAt <= now) {
      return {
        ...item,
        enabled: false,
        suppressedReason:
          item.type === "reminder"
            ? SMS_SUPPRESS_REASON.PAST_REMINDER
            : SMS_SUPPRESS_REASON.PAST_INITIAL,
      };
    }
    return { ...item, suppressedReason: null };
  });

  // Gap rule — needs the confirmation of the same appointment.
  const initialByGroup = {};
  guarded.forEach((item) => {
    if (item.type !== "initial") return;
    const key = smsPreviewGroupKey(item.id);
    if (key) initialByGroup[key] = item;
  });

  return guarded.map((item) => {
    if (item.type !== "reminder" || item.suppressedReason) return item;
    // An explicit re-check of a too-close reminder is honoured.
    if (item.gapOverride) return item;

    const initial = initialByGroup[smsPreviewGroupKey(item.id)];
    if (!initial || !initial.enabled) return item;

    const initialAt = initial.isImmediate ? now : parsePreviewSendTime(initial);
    const reminderAt = parsePreviewSendTime(item);
    if (!initialAt || !reminderAt) return item;

    if (reminderAt.getTime() - initialAt.getTime() < MIN_SMS_GAP_MS) {
      return { ...item, enabled: false, suppressedReason: SMS_SUPPRESS_REASON.TOO_CLOSE };
    }
    return item;
  });
};

// Initial/reminder confirmation SMS text. Single source for the create-flow
// preview and the late "send now" flow so the patient always sees identical
// wording. Drops the treatment type when it would push the message past the
// SMS length cap (link included).
export const buildConfirmationSmsText = ({
  type, // 'initial' | 'reminder'
  patientName,
  clinicName,
  treatmentTypeName,
  aptDate,
  aptTime,
  language = "he",
}) => {
  const { message, omitted } = renderSms(
    type === "initial" ? "appointment.initial" : "appointment.reminder",
    language,
    {
      name: patientName,
      clinicName: clinicName || "",
      treatmentTypeName: treatmentTypeName || "",
      date: formatSmsDate(aptDate, language),
      time: aptTime,
    },
    // No real link yet — reserve its characters so the preview drops the
    // treatment type exactly where the send would. The preview is never
    // truncated; the therapist sees the full wording.
    { linkChars: SMS_LINK_CHARS, max: SMS_MAX_LENGTH, truncate: false },
  );

  return {
    message,
    treatmentTypeOmitted: omitted.includes("treatmentTypeName"),
  };
};

// Series confirmation SMS text. Shared by the preview and the actual send so
// the patient never sees wording that differs from what the therapist approved.
export const buildSeriesSmsMessage = ({
  patientName,
  count,
  hebrewStartDate,
  // `hasLink` picks the closing clause: the confirmation URL is appended by the
  // caller when it sends, but the preview shows the wording without it.
  hasLink = false,
  frequency,
  baseDate,
  baseTime,
  secondDay = "",
  secondTime = "",
  language = "he",
}) =>
  renderSms(
    "appointment.series",
    language,
    {
      name: patientName,
      count,
      startDate: hebrewStartDate,
      frequency,
      baseDayIndex: new Date(`${baseDate}T00:00:00`).getDay(),
      baseTime,
      secondDayIndex: secondDay,
      secondTime,
    },
    // The series text is built here and handed to the server as customMessage,
    // which appends the link itself — so nothing is appended or truncated here,
    // but the closing clause still has to match whether a link will follow.
    { truncate: false, hasLink },
  ).message;

// Default scheduled send time for the reminder SMS
export const calcReminderSchedule = (therapistSmsSettings, aptDate, aptTime) => {
  const reminderHours = therapistSmsSettings?.reminderHoursBefore || 2;
  const [h, m] = aptTime.split(":");
  const dt = new Date(aptDate);
  dt.setHours(parseInt(h, 10), parseInt(m, 10), 0, 0);
  dt.setTime(dt.getTime() - reminderHours * 60 * 60 * 1000);
  const sd = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
  const st = `${String(dt.getHours()).padStart(2, "0")}:${String(dt.getMinutes()).padStart(2, "0")}`;
  return { scheduledDate: sd, scheduledTime: st };
};

// Default scheduled send time for the initial/confirmation SMS when the
// therapist chose to send it X hours before the appointment.
export const calcInitialSchedule = (therapistSmsSettings, aptDate, aptTime) => {
  const initialHours = therapistSmsSettings?.initialHoursBefore || 3;
  const [h, m] = aptTime.split(":");
  const dt = new Date(aptDate);
  dt.setHours(parseInt(h, 10), parseInt(m, 10), 0, 0);
  dt.setTime(dt.getTime() - initialHours * 60 * 60 * 1000);
  const sd = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
  const st = `${String(dt.getHours()).padStart(2, "0")}:${String(dt.getMinutes()).padStart(2, "0")}`;
  return { scheduledDate: sd, scheduledTime: st };
};

// "Now" date/time for immediate sending defaults
export const getNowSchedule = (now = new Date()) => {
  const sd = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
  const st = `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;
  return { scheduledDate: sd, scheduledTime: st };
};

// Build the SMS preview items for the create-flow preview screen.
// Pure: every piece of component state is passed in explicitly.
//   formData        — patientName, date, startTime, sendSms, isRecurring,
//                     treatmentTypeId, recurringFrequency, recurringSecondDay/Time
//   seriesItems     — seriesPreview.items ({ date, time, dateStr, ... }[])
export const buildSmsPreview = ({
  formData,
  patientPhone,
  clinicName,
  therapistSmsSettings,
  treatmentTypes,
  isPastDateTime,
  seriesItems = [],
  now = new Date(),
  // Patient communication language. Only the message bodies change — the item
  // labels are therapist-facing UI and stay Hebrew.
  language = "he",
}) => {
  const items = [];
  const patientName = formData.patientName;
  const includeTreatmentType = therapistSmsSettings?.includeTreatmentTypeInSms === true;
  const treatmentTypeName = includeTreatmentType && formData.treatmentTypeId
    ? treatmentTypes.find((t) => t.id === formData.treatmentTypeId)?.name || ""
    : "";

  if (!patientPhone || isPastDateTime) return items;

  // Which messages the therapist enabled in settings. A message that is
  // disabled in settings is not shown in the preview at all — the therapist
  // only sees (and can toggle per-appointment) the messages they turned on.
  // Reminder depends on the confirmation being enabled.
  const settingsInitialOn = therapistSmsSettings?.sendOnCreate !== false;
  const settingsReminderOn =
    settingsInitialOn && therapistSmsSettings?.sendReminderEnabled !== false;
  const initEnabled = formData.sendSms;
  const remEnabled = formData.sendSms;
  const initImmediate = therapistSmsSettings?.initialTiming !== "before";

  const buildMessage = (type, aptDate, aptTime) =>
    buildConfirmationSmsText({
      type,
      patientName,
      clinicName: clinicName || "",
      treatmentTypeName,
      aptDate,
      aptTime,
      language,
    });

  if (formData.isRecurring) {
    // Drive the preview off seriesItems so the per-meeting SMS scheduling
    // indices line up exactly with the meetings that get created.
    const hebrewDate = formatSmsDate(formData.date, language);
    const nowSched = getNowSchedule(now);

    if (settingsInitialOn) {
      items.push({
        id: "series-initial",
        type: "series",
        label: "הודעת סדרת פגישות",
        appointmentDate: formData.date,
        appointmentTime: formData.startTime,
        scheduledDate: nowSched.scheduledDate,
        scheduledTime: nowSched.scheduledTime,
        scheduledDayName: HEBREW_DAYS_FULL[new Date(nowSched.scheduledDate).getDay()],
        isImmediate: true,
        enabled: initEnabled,
        message: buildSeriesSmsMessage({
          patientName,
          count: seriesItems.length,
          hebrewStartDate: hebrewDate,
          language,
          frequency: formData.recurringFrequency,
          baseDate: formData.date,
          baseTime: formData.startTime,
          secondDay: formData.recurringSecondDay,
          secondTime: formData.recurringSecondTime,
        }),
      });
    }

    seriesItems.forEach((seriesItem, idx) => {
      const finalDate = seriesItem.dateStr;
      const finalTime = seriesItem.time;
      const aptDayName = HEBREW_DAYS_FULL[seriesItem.date.getDay()];
      const initSched = initImmediate
        ? getNowSchedule(now)
        : calcInitialSchedule(therapistSmsSettings, finalDate, finalTime);
      const remSched = calcReminderSchedule(therapistSmsSettings, finalDate, finalTime);

      if (settingsInitialOn) {
        const initResult = buildMessage("initial", finalDate, finalTime);
        items.push({
          id: `apt-${idx}-initial`,
          type: "initial",
          label: `אישור הגעה - פגישה ${idx + 1}`,
          appointmentDate: finalDate,
          appointmentTime: finalTime,
          appointmentDayName: aptDayName,
          scheduledDate: initSched.scheduledDate,
          scheduledTime: initSched.scheduledTime,
          scheduledDayName: HEBREW_DAYS_FULL[new Date(initSched.scheduledDate).getDay()],
          isImmediate: initImmediate,
          enabled: initEnabled,
          message: initResult.message,
          treatmentTypeOmitted: initResult.treatmentTypeOmitted,
        });
      }

      if (settingsReminderOn) {
        const remResult = buildMessage("reminder", finalDate, finalTime);
        items.push({
          id: `apt-${idx}-reminder`,
          type: "reminder",
          label: `תזכורת - פגישה ${idx + 1}`,
          appointmentDate: finalDate,
          appointmentTime: finalTime,
          appointmentDayName: aptDayName,
          scheduledDate: remSched.scheduledDate,
          scheduledTime: remSched.scheduledTime,
          scheduledDayName: HEBREW_DAYS_FULL[new Date(remSched.scheduledDate).getDay()],
          isImmediate: false,
          enabled: remEnabled,
          message: remResult.message,
          treatmentTypeOmitted: remResult.treatmentTypeOmitted,
        });
      }
    });
  } else {
    const aptDayName = HEBREW_DAYS_FULL[new Date(formData.date).getDay()];
    const initSched = initImmediate
      ? getNowSchedule(now)
      : calcInitialSchedule(therapistSmsSettings, formData.date, formData.startTime);
    const remSched = calcReminderSchedule(therapistSmsSettings, formData.date, formData.startTime);

    if (settingsInitialOn) {
      const singleInitResult = buildMessage("initial", formData.date, formData.startTime);
      items.push({
        id: "single-initial",
        type: "initial",
        label: "הודעת אישור הגעה",
        appointmentDate: formData.date,
        appointmentTime: formData.startTime,
        appointmentDayName: aptDayName,
        scheduledDate: initSched.scheduledDate,
        scheduledTime: initSched.scheduledTime,
        scheduledDayName: HEBREW_DAYS_FULL[new Date(initSched.scheduledDate).getDay()],
        isImmediate: initImmediate,
        enabled: initEnabled,
        message: singleInitResult.message,
        treatmentTypeOmitted: singleInitResult.treatmentTypeOmitted,
      });
    }

    if (settingsReminderOn) {
      const singleRemResult = buildMessage("reminder", formData.date, formData.startTime);
      items.push({
        id: "single-reminder",
        type: "reminder",
        label: "הודעת תזכורת",
        appointmentDate: formData.date,
        appointmentTime: formData.startTime,
        appointmentDayName: aptDayName,
        scheduledDate: remSched.scheduledDate,
        scheduledTime: remSched.scheduledTime,
        scheduledDayName: HEBREW_DAYS_FULL[new Date(remSched.scheduledDate).getDay()],
        isImmediate: false,
        enabled: remEnabled,
        message: singleRemResult.message,
        treatmentTypeOmitted: singleRemResult.treatmentTypeOmitted,
      });
    }
  }

  return applySmsScheduleGuards(items, now);
};

// Translate approved preview items into the SMS-scheduling fields persisted on
// the appointment doc(s). Mirrors getSchedulingFromPreview in AppointmentModal.
// Single → { initialSmsEnabled, initialSmsScheduledAt, reminderSmsEnabled,
//            reminderSmsScheduledAt, reminderSmsGapOverride }
// Series → { smsScheduling: { [idx]: {…same per occurrence} } }
export const getSchedulingFromPreview = (smsPreviewItems, isRecurring) => {
  if (!smsPreviewItems.length) return {};
  // A message the guards suppressed (past send time / too close to the
  // confirmation) keeps its *enabled* flag so that moving the meeting later
  // can revive it — it just carries no send time, so nothing is scheduled.
  const enabledFlag = (item) =>
    item ? item.enabled || !!item.suppressedReason : false;
  if (isRecurring) {
    const smsScheduling = {};
    smsPreviewItems.forEach((item) => {
      const match = item.id.match(/^apt-(\d+)-(initial|reminder)$/);
      if (!match) return;
      const idx = parseInt(match[1], 10);
      const smsType = match[2];
      // Default both messages to disabled; only the items present in the
      // preview (i.e. enabled in settings) flip their flag on below.
      if (!smsScheduling[idx]) {
        smsScheduling[idx] = {
          initialSmsEnabled: false,
          initialSmsScheduledAt: null,
          reminderSmsEnabled: false,
          reminderSmsScheduledAt: null,
        };
      }
      if (smsType === "initial") {
        smsScheduling[idx].initialSmsEnabled = enabledFlag(item);
        smsScheduling[idx].initialSmsScheduledAt = item.enabled && !item.isImmediate ? `${item.scheduledDate}T${item.scheduledTime}:00` : null;
      } else {
        smsScheduling[idx].reminderSmsEnabled = enabledFlag(item);
        smsScheduling[idx].reminderSmsScheduledAt = item.enabled ? `${item.scheduledDate}T${item.scheduledTime}:00` : null;
        // Persisted so the server's gap check (evaluateReminderSuppression)
        // doesn't silently undo an explicit re-enable of a too-close reminder.
        smsScheduling[idx].reminderSmsGapOverride = item.enabled && item.gapOverride === true;
      }
    });
    return { smsScheduling };
  }
  const initialItem = smsPreviewItems.find((i) => i.id === "single-initial");
  const reminderItem = smsPreviewItems.find((i) => i.id === "single-reminder");
  // Absent item = message disabled in settings → not sent for this appointment.
  const initialEnabled = initialItem ? initialItem.enabled : false;
  const reminderEnabled = reminderItem ? reminderItem.enabled : false;
  return {
    initialSmsEnabled: enabledFlag(initialItem),
    initialSmsScheduledAt: initialEnabled && initialItem && !initialItem.isImmediate ? `${initialItem.scheduledDate}T${initialItem.scheduledTime}:00` : null,
    reminderSmsEnabled: enabledFlag(reminderItem),
    reminderSmsScheduledAt: reminderEnabled && reminderItem ? `${reminderItem.scheduledDate}T${reminderItem.scheduledTime}:00` : null,
    // Persisted so the server's gap check (evaluateReminderSuppression)
    // doesn't silently undo an explicit re-enable of a too-close reminder.
    reminderSmsGapOverride: reminderEnabled && reminderItem?.gapOverride === true,
  };
};
